/**
 * Centralized API Configuration
 * Single source of truth for API URLs and environment settings
 */

class ApiConfig {
  constructor() {
    this._apiUrl = null;
    this._initialized = false;
  }

  /**
   * Get the current API URL
   * @returns {string} The API URL
   */
  getApiUrl() {
    if (!this._initialized) {
      this._initialize();
    }
    return this._apiUrl;
  }

  /**
   * Get API URL with version prefix
   * @param {string} version - API version (default: 'v0')
   * @returns {string} The versioned API URL
   */
  getVersionedApiUrl(version = undefined) {
    let resolved = version;
    try {
      // eslint-disable-next-line no-undef
      if (!resolved && typeof __API_VERSION__ !== 'undefined' && __API_VERSION__) {
        // eslint-disable-next-line no-undef
        resolved = __API_VERSION__;
      }
    } catch {}
    if (!resolved) {
      // Default: v1 in non-local; v0 in local (matches vite define)
      resolved = this.isLocalDev() ? 'v1' : 'v1';
    }
    return `${this.getApiUrl()}/${resolved}`;
  }

  /**
   * Check if we're in local development mode
   * @returns {boolean} True if in local development
   */
  isLocalDev() {
    try {
      // Vite dev flag
      if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
        return true;
      }
    } catch {}
    try {
      const host = window.location?.hostname || '';
      if (host === 'localhost' || host === '127.0.0.1') return true;
      const port = window.location?.port;
      if (port && ['3000', '5173', '8787'].includes(port)) return true;
    } catch {}
    if (typeof window !== 'undefined' && typeof window.API_URL === 'string' && /localhost|127\.0\.0\.1/.test(window.API_URL)) {
      return true;
    }
    return window.LOCAL_DEV === true || window.ENVIRONMENT === 'development';
  }

  /**
   * Initialize the configuration
   * @private
   */
  _initialize() {
    // Priority order:
    // 1. Local dev: prefer explicit backend URL if provided via Vite define (__API_URL__) or window.API_URL
    //    Otherwise, use relative '' so '/v0' hits the dev proxy
    // 2. Non-local: use window.API_URL (if set), else production cloud URL
    if (this.isLocalDev()) {
      try {
        // eslint-disable-next-line no-undef
        if (typeof __API_URL__ !== 'undefined' && __API_URL__) {
          // eslint-disable-next-line no-undef
          this._apiUrl = __API_URL__;
        } else if (typeof window !== 'undefined' && window.API_URL) {
          this._apiUrl = window.API_URL;
        } else {
          this._apiUrl = '';
        }
      } catch {
        this._apiUrl = window.API_URL || '';
      }
    } else {
      // Prefer same-origin API in production/staging to match worker routing
      // Fallback to explicitly configured API_URL, then legacy api subdomain
      const sameOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
      this._apiUrl = window.API_URL || sameOrigin;
    }
    this._initialized = true;

    console.log(`[ApiConfig] Initialized with API URL: ${this._apiUrl}`);
    if (this.isLocalDev()) {
      console.log('[ApiConfig] 🔧 Local development mode detected');
    }
  }

  /**
   * Force reinitialize (useful for testing)
   */
  reinitialize() {
    this._initialized = false;
    this._initialize();
  }
}

// Export singleton instance
export const apiConfig = new ApiConfig();

// Convenience exports
export const getApiUrl = () => apiConfig.getApiUrl();
export const getVersionedApiUrl = (version) => apiConfig.getVersionedApiUrl(version);
export const isLocalDev = () => apiConfig.isLocalDev();